/**
 * ATL-2052 — the session mirror-intent report: `mirror_intent_session`.
 *
 * WHY. ATL-1459's mirror intent (`global/Firebase/mirrorIntent.ts`) is switched on per organization by
 * Remote Config `mirror_intent_org_allowlist`. Until now nothing on a till said whether it had acted
 * on that switch: the only signal was the ABSENCE of `mirror_intent_*` events, which reads the same
 * for a till that is ON and never crashed, a till that never received the value (ATL-911: the Remote
 * Config SDK can report a successful fetch and read empty), and a till whose breaker tripped. A canary
 * cannot be certified on that. This is the question ATL-887 taught this project to ask of every flip:
 * not "was the flag set", but "how many tills acted on it". Modelled on `write_mode_pinned`
 * (`writeModeTelemetry.ts`).
 *
 * WHEN. Once per (process, organization), after sign-in, once the boot Remote Config fetch has
 * settled (`remoteConfigReady`, which never rejects) — so the value reported is the one the first
 * sale of the session will see. Never per sale.
 *
 * WHAT. `mirror_intent_active` (what `isMirrorIntentActive` answers for this organization), the facts
 * it is made of (`allowlisted`, `covered_by` — `allowlist` | `sb-readers` | null, ATL-2052 —, the
 * `mirror_intent_sb_readers` switch, whether the org reads Supabase, the breaker), and where the policy
 * came from (`mirrorIntentPolicy.ts`):
 *   - `device-rc`  — this device's Remote Config read a mirror-intent key;
 *   - `backend`    — it read both empty and the ATL-911 backend fallback answered;
 *   - `none`       — nothing answered: the keys are unset everywhere, or the device could not reach
 *                    the backend either.
 * Plus the device's Remote Config fetch outcome and last successful fetch time, and the template
 * VERSION — known only through the backend (the client SDKs do not expose it), else `null`.
 *
 * Before reading, the report PRIMES the backend fallback when the device read both keys empty
 * (`primeMirrorIntentPolicyFromBackend`), so the value it reports — and the first sale's — is the one
 * an ATL-911 device can actually get.
 *
 * No PII and no record ids: the organization's Firestore id (the PostHog `organization` group key),
 * booleans, enums, epoch ms.
 */
import { captureOperationalEvent, type OperationalProperties } from './operationalTelemetry'
import { runningBundleProperties } from './writeBundleCapability'
import type { MirrorIntentActivation } from '../Firebase/mirrorIntent'
import type { MirrorIntentPolicy, MirrorIntentPolicySource } from '../Firebase/mirrorIntentPolicy'

export const MIRROR_INTENT_SESSION_EVENT = 'mirror_intent_session'

/** Where the policy the session acts on came from. */
export type MirrorIntentDecisionSource = MirrorIntentPolicySource

export interface MirrorIntentSessionFacts {
  organizationId: string
  activation: MirrorIntentActivation
  /** The policy as the session reads it (device RC, else the backend fallback). */
  policy: Pick<MirrorIntentPolicy, 'source' | 'templateVersion'>
  /** How the boot Remote Config fetch settled. */
  rcStatus: 'fetched' | 'cache' | 'error' | 'unknown'
  /** Epoch ms of the last successful Remote Config fetch, or `<= 0` for never. */
  rcFetchTimeMillis: number
  /** The read-cutover decision of this session (`shouldUseSupabase()` at startup). */
  sessionReadsSupabase: boolean
}

export const buildMirrorIntentSessionProperties = (facts: MirrorIntentSessionFacts): OperationalProperties => ({
  organization_id: facts.organizationId,
  mirror_intent_active: facts.activation.active,
  mirror_intent_allowlisted: facts.activation.allowlisted,
  mirror_intent_covered_by: facts.activation.coveredBy,
  mirror_intent_sb_readers_on: facts.activation.sbReadersOn,
  org_reads_supabase: facts.activation.orgReadsSupabase,
  mirror_intent_decision_source: facts.policy.source,
  mirror_intent_breaker: facts.activation.breakerTripped ?? 'closed',
  session_reads_supabase: facts.sessionReadsSupabase,
  rc_status: facts.rcStatus,
  rc_fetch_time_ms: facts.rcFetchTimeMillis > 0 ? facts.rcFetchTimeMillis : null,
  // Only the backend knows it (the client SDKs do not expose it); an explicit null otherwise.
  rc_template_version: facts.policy.templateVersion,
  // The RUNNING bundle, in the keys `ota-coverage` joins on (as `write_mode_pinned` does).
  ...runningBundleProperties(),
})

export interface MirrorIntentSessionDeps {
  remoteConfigReady: Promise<'fetched' | 'cache' | 'error'>
  /** Make the backend fallback available when the device reads the keys empty (never rejects). */
  prime: () => Promise<unknown>
  describe: (organizationId: string) => MirrorIntentActivation
  policy: () => Pick<MirrorIntentPolicy, 'source' | 'templateVersion'>
  rcFetchTimeMillis: () => number
  capture?: (event: string, properties: OperationalProperties) => void
}

/** Organizations this PROCESS has already reported (one event per launch, not per data reload). */
const reported = new Set<string>()

/** TEST-ONLY. */
export const resetMirrorIntentSessionReportForTests = (): void => {
  reported.clear()
}

function appDeps(): MirrorIntentSessionDeps {
  // Lazily required: the Firestore module binds the platform SDK, and the journal reaches the app graph.
  /* eslint-disable @typescript-eslint/no-var-requires */
  const firestore = require('../Firebase/firebaseFirestore') as {
    remoteConfigReady: Promise<'fetched' | 'cache' | 'error'>
    getRemoteConfigValue: (key: string) => string
    getRemoteConfigFetchTimeMillis: () => number
  }
  const mirrorIntent = require('../Firebase/mirrorIntent') as {
    describeMirrorIntentActivation: (organizationId: string) => MirrorIntentActivation
  }
  const policy = require('../Firebase/mirrorIntentPolicy') as {
    readMirrorIntentPolicy: (read: (key: string) => unknown) => MirrorIntentPolicy
  }
  /* eslint-enable @typescript-eslint/no-var-requires */
  return {
    remoteConfigReady: firestore.remoteConfigReady,
    // Required at call time: `dualWrite.ts` pulls the API client graph, and a failure to load it must
    // cost only the fallback (the report then says `none`), never the report.
    prime: () => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const dualWrite = require('../Firebase/dualWrite') as { primeMirrorIntentPolicyFromBackend: () => Promise<unknown> }
      return dualWrite.primeMirrorIntentPolicyFromBackend()
    },
    describe: mirrorIntent.describeMirrorIntentActivation,
    policy: () => policy.readMirrorIntentPolicy(firestore.getRemoteConfigValue),
    rcFetchTimeMillis: firestore.getRemoteConfigFetchTimeMillis,
  }
}

/**
 * Report this session's mirror-intent state once the boot Remote Config fetch has settled. Never
 * rejects and never throws: telemetry must not be able to break the startup it rides on.
 *
 * @returns whether an event was captured (false: already reported for this organization, no
 *          organization, or the report itself failed).
 */
export const reportMirrorIntentSession = async (
  input: { organizationId: string | null | undefined; readsSupabase: boolean },
  deps?: MirrorIntentSessionDeps,
): Promise<boolean> => {
  try {
    const organizationId = input.organizationId
    if (typeof organizationId !== 'string' || organizationId.length === 0) return false
    if (reported.has(organizationId)) return false
    reported.add(organizationId)
    const d = deps ?? appDeps()
    let rcStatus: MirrorIntentSessionFacts['rcStatus']
    try {
      rcStatus = await d.remoteConfigReady
    } catch {
      rcStatus = 'unknown'
    }
    try {
      await d.prime()
    } catch {
      // The report goes out with whatever the session can read.
    }
    let policy: MirrorIntentSessionFacts['policy']
    try {
      policy = d.policy()
    } catch {
      policy = { source: 'none', templateVersion: null }
    }
    let rcFetchTimeMillis = -1
    try {
      rcFetchTimeMillis = d.rcFetchTimeMillis()
    } catch {
      rcFetchTimeMillis = -1
    }
    const properties = buildMirrorIntentSessionProperties({
      organizationId,
      activation: d.describe(organizationId),
      policy,
      rcStatus,
      rcFetchTimeMillis,
      sessionReadsSupabase: input.readsSupabase,
    })
    ;(d.capture ?? captureOperationalEvent)(MIRROR_INTENT_SESSION_EVENT, properties)
    return true
  } catch {
    return false
  }
}
